import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Tag, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export interface PublicCoupon {
  code: string;
  discount_type: string;
  discount_value: number;
  min_order_value: number;
  max_discount: number | null;
  valid_to: string | null;
}

export const describeCoupon = (c: PublicCoupon) => {
  const off =
    c.discount_type === "PERCENTAGE"
      ? `${c.discount_value}% off`
      : `₹${c.discount_value.toLocaleString("en-IN")} off`;
  const parts = [off];
  if (c.min_order_value > 0) parts.push(`min ₹${c.min_order_value.toLocaleString("en-IN")}`);
  else if (c.max_discount) parts.push(`up to ₹${c.max_discount.toLocaleString("en-IN")}`);
  return parts.join(" · ");
};

interface CouponOffersProps {
  onSelect?: (code: string) => void;
  onLoaded?: (coupons: PublicCoupon[]) => void;
}

const CouponOffers = ({ onSelect, onLoaded }: CouponOffersProps) => {
  const [coupons, setCoupons] = useState<PublicCoupon[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    axios
      .get("/.netlify/functions/get-active-coupons")
      .then((res) => {
        if (!active || !res.data?.success) return;
        const list: PublicCoupon[] = res.data.data || [];
        setCoupons(list);
        onLoaded?.(list);
      })
      .catch(() => {
        /* offers are optional — stay hidden on failure */
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopy = async (code: string) => {
    onSelect?.(code);
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied((c) => (c === code ? null : c)), 2000);
      toast.success(`${code} copied — apply at checkout`);
    } catch {
      toast.success(`${code} will be applied at checkout`);
    }
  };

  if (coupons.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="flex flex-wrap justify-center gap-3 mb-12"
    >
      {coupons.map((c) => (
        <button
          key={c.code}
          type="button"
          onClick={() => handleCopy(c.code)}
          className="group flex items-center gap-3 rounded-full border border-accent/40 bg-card/60 backdrop-blur px-4 py-2 text-left transition-all hover:border-accent hover:shadow-[var(--shadow-glow-teal)]"
        >
          <Tag className="w-4 h-4 text-accent shrink-0" />
          <span className="font-heading text-sm font-semibold text-foreground tracking-wide">
            {c.code}
          </span>
          <span className="font-body text-xs text-muted-foreground">
            {describeCoupon(c)}
            {c.valid_to ? ` · ends ${format(new Date(c.valid_to), "MMM d")}` : ""}
          </span>
          {copied === c.code ? (
            <Check className="w-3.5 h-3.5 text-accent shrink-0" />
          ) : (
            <Copy className="w-3.5 h-3.5 text-muted-foreground group-hover:text-accent shrink-0" />
          )}
        </button>
      ))}
    </motion.div>
  );
};

export default CouponOffers;
